// Deterministic mock sales dataset for the Sales module.

import { fetchCategoriesApi } from "../api/fetchCategories";
import { fetchInvoiceApi } from "../api/fetchInvoiceApi";
import { fetchSalesApi } from "../api/fetchSales";
import { fetchSalesAgentsApi } from "../api/fetchSalesAgents";
import { fetchStoresApi } from "../api/fetchStores";

/**
 * @typedef {Object} SalesAgent
 * @property {string} id
 * @property {string} name
 * @property {StoreName} store
 */

/**
 * @typedef {Object} TopProduct
 * @property {string} friendlycode
 * @property {string} name
 * @property {string} category
 * @property {number} unitsSold
 * @property {number} revenue
 */

/**
 * @typedef {Object} MonthEntry
 * @property {string} key
 * @property {string} label
 * @property {Date} date
 */

// Returns last N months labels ending with the current month (short label + year).
/**
 * @param {string} startDate
 * @param {string} endDate
 * @returns {MonthEntry[]}
 */
export function lastNMonths(startDate, endDate) {
  // const now = new Date();

  const endDateObj = new Date(endDate);
  const startDateObj = new Date(startDate);

  const yearDif = endDateObj.getFullYear() - startDateObj.getFullYear();
  const monthDiff = endDateObj.getMonth() - startDateObj.getMonth();

  const totalMonths = yearDif * 12 + monthDiff + 1;

  /**@type {number} */
  const n = Math.max(1, totalMonths);

  /** @type {MonthEntry[]} */
  const out = [];

  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(endDateObj.getFullYear(), endDateObj.getMonth() - i, 1);
    out.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString("en", { month: "short", year: "2-digit" }),
      date: d,
    });
  }
  return out;
}

// Generates a monthly matrix: for each month, revenue per store.
/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/StoreModel").StoreModel[]} stores
 * @param {import("../models/SalesModel").SalesModel[]} sales
 * @returns {Record<string, number | string>[]}
 */
export function monthlyRevenueByStore(startDate, endDate, stores, sales) {
  const list = lastNMonths(startDate, endDate);

  // [{month: 'Oct 25', ACCOUNTS: 472003, MAIN BAR: 737299, RESTAURANT: 492959, PRO SHOP: 201260, MINI MART: 297108}]

  let l = list.map((m, idx) => {
    /** @type {Record<string, number | string>} */
    const row = { month: m.label };
    stores.forEach((s, si) => {
      let t = sales.reduce((sum, item) => {
        if (s.storecode !== item.storecode || !item.clientdateclienttime) {
          return sum;
        }
        const itemDate = new Date(item.clientdateclienttime);
        const matchesDate =
          itemDate.getFullYear() === m.date.getFullYear() &&
          itemDate.getMonth() === m.date.getMonth();

        return matchesDate ? sum + (item.total || 0) : sum;
      }, 0);
      row[s.storename] = t;
    });

    return row;
  });

  // console.log(l);

  return l;
}

/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/CategoryModel").CategoryModel[]} categories
 * @param {import("../models/SalesModel").SalesModel[]} sales
 * @returns {Record<string, number | string>[]}
 */
export function monthlyRevenueByCategory(
  startDate,
  endDate,
  categories,
  sales,
) {
  // const rand = seeded(7);
  const list = lastNMonths(startDate, endDate);
  return list.map((m, idx) => {
    // const growth = 1 + idx * 0.012;
    /** @type {Record<string, number | string>} */
    const row = { month: m.label };

    (categories || []).forEach((c, ci) => {
      let t = (sales || []).reduce((sum, s) => {
        if (`${s.maincatcode}` !== c.id || !s.clientdateclienttime) {
          return sum;
        }
        const itemDate = new Date(s.clientdateclienttime);
        const matchesDate =
          itemDate.getFullYear() === m.date.getFullYear() &&
          itemDate.getMonth() === m.date.getMonth();

        return matchesDate ? sum + (s.total || 0) : sum;
      }, 0);

      row[c.name] = t;
    });
    return row;
  });
}

/**
 * @typedef {Object} MonthlyTotal
 * @property {string} month
 * @property {number} revenue
 * @property {number} orders
 */

/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/InvoiceModel").InvoiceModel[]} invoices
 * @param {import("../models/SalesModel").SalesModel[]} sales
 * @returns {MonthlyTotal[]}
 */
export function monthlyTotals(startDate, endDate, invoices, sales) {
  const list = lastNMonths(startDate, endDate);

  // // by invoices
  // let l = list.map((m, idx) => {
  //   /** @type {number} */
  //   let r = 0;
  //   /** @type {number} */
  //   let o = 0;

  //   (invoices || []).forEach((s, i) => {
  //     if (!s.sdate) {
  //       return;
  //     }

  //     const itemDate = new Date(s.sdate);
  //     const matchesDate =
  //       itemDate.getFullYear() === m.date.getFullYear() &&
  //       itemDate.getMonth() === m.date.getMonth();

  //     if (matchesDate) {
  //       r = r + s.sttlsales;
  //       o = o + 1;
  //     }
  //   });

  //   return { month: /** @type {string} */ (m.label), revenue: r, orders: o };
  // });
  //
  // by sales
  let l = list.map((m, idx) => {
    /** @type {number} */
    let r = 0;
    /** @type {number} */
    let o = 0;

    (sales || []).forEach((s, i) => {
      if (!s.sdate) {
        return;
      }

      const itemDate = new Date(s.sdate);
      const matchesDate =
        itemDate.getFullYear() === m.date.getFullYear() &&
        itemDate.getMonth() === m.date.getMonth();

      if (matchesDate) {
        r = r + s.total;
        o = o + 1;
      }
    });

    return { month: /** @type {string} */ (m.label), revenue: r, orders: o };
  });

  // console.log(l);
  // [{month: 'Sep 25', revenue: NaN, orders: NaN}]
  return l;
}

/**
 * @typedef {import("../models/SalesAgentsModel").SalesAgentModel & {
 *   revenue: number,
 *   orders: number,
 *   target: number,
 *   attainment: number,
 *   avgBasket: number,
 * }} AgentPerformance
 */

/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/SalesAgentsModel").SalesAgentModel[]} salesagents
 * @param {import("../models/InvoiceModel").InvoiceModel[]} invoices
 * @returns {AgentPerformance[]}
 */
export function agentPerformance(startDate, endDate, salesagents, invoices) {
  const list = lastNMonths(startDate, endDate);

  // console.log(invoices);
  // console.log(salesagents);

  // let firstDate = list[0].date;
  // let lastDate = list[list.length - 1].date;
  let firstDate = new Date(startDate);
  let lastDate = new Date(endDate);

  let l = salesagents
    .map((a) => {
      const { revenue, orders } = (invoices || []).reduce(
        (acc, s) => {
          if (Number(a.employeescode) !== s.salesagentcode || !s.sdate) {
            return acc;
          }

          const itemDate = new Date(s.sdate);

          if (itemDate >= firstDate && itemDate <= lastDate) {
            acc.revenue += s.sttlsales || 0;
            acc.orders += 1;
          }

          return acc;
        },
        { revenue: 0, orders: 0 },
      );

      const target = 0;
      return {
        ...a,
        revenue,
        orders,
        target,
        attainment: Math.round((revenue / target) * 100),
        avgBasket: Math.round(revenue / Math.max(orders, 1)),
      };
    })
    .sort((a, b) => b.revenue - a.revenue);

  // console.log(l);
  // [attainment:100,avgBasket:996,id:"A06",name:"Lucy Njeri"
  // orders:876,revenue:872615,store:"MAIN BAR",target:874825]

  return l;
}

/**
 * @typedef {Object} PaymentMixEntry
 * @property {string} name
 * @property {number} value
 */

/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/InvoiceModel").InvoiceModel[]} invoices
 * @returns {PaymentMixEntry[]}
 */
export function paymentMix(startDate, endDate, invoices) {
  const list = lastNMonths(startDate, endDate);

  // let firstDate = list[0].date;
  // let lastDate = list[list.length - 1].date;

  let firstDate = new Date(startDate);
  let lastDate = new Date(endDate);

  const groupedTotals = (invoices || []).reduce((acc, item) => {
    if (!item.sdate) return acc;

    const itemDate = new Date(item.sdate);

    if (itemDate >= firstDate && itemDate <= lastDate) {
      const type = item.vchtype || "Unknown";
      const amount = item.sttlsales || 0;

      acc[type] = (acc[type] || 0) + amount;
    }

    return acc;
  }, /** @type {Object<string, number>} */ ({}));

  // console.log(groupedTotals);

  const l = Object.entries(groupedTotals).map(([name, value]) => ({
    name: name,
    value: value,
  }));

  // console.log(l);
  // [{name: 'Cash', value: 0}]
  return l;
  // return [];
}

/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/SalesModel").SalesModel[]} sales
 * @param {import("../models/CategoryModel").CategoryModel[]} categories
 * @returns {TopProduct[]}
 */
export function getTopProducts(startDate, endDate, sales, categories) {
  const list = lastNMonths(startDate, endDate);

  // let firstDate = list[0].date;
  // let lastDate = list[list.length - 1].date;

  let firstDate = new Date(startDate);
  let lastDate = new Date(endDate);

  /** @type {Record<string, TopProduct>} */
  const aggrMaps = (sales || []).reduce((acc, item) => {
    if (!item.clientdateclienttime) return acc;

    const itemDate = new Date(item.clientdateclienttime);

    if (itemDate >= firstDate && itemDate <= lastDate) {
      const itemKey = String(item.itemcode);
      const qty = item.qty || 0;
      const total = item.total || 0;

      if (!acc[itemKey]) {
        const categoryName =
          categories.find((c, i) => Number(c.id) === item.maincatcode)?.name ||
          "Uncategorized";

        acc[itemKey] = {
          friendlycode: itemKey,
          name: (item.itemname || "Unknown Item").trim(),
          category: categoryName,
          unitsSold: 0,
          revenue: 0,
        };
      }

      acc[itemKey].unitsSold += qty;
      acc[itemKey].revenue += total;
    }

    return acc;
  }, /** @type {Record<string, TopProduct>} */ ({}));

  // console.log(aggrMaps);

  return Object.values(aggrMaps)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
}

/**
 * @typedef {Object} HourlyEntry
 * @property {string} hour
 * @property {number} revenue
 * @property {number} orders
 */

/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/SalesModel").SalesModel[]} sales
 * @returns {HourlyEntry[]}
 */
export function hourlyDistribution(startDate, endDate, sales) {
  const list = lastNMonths(startDate, endDate);

  // let startDateB = list[0].date;
  // let endDateB = list[list.length - 1].date;
  let startDateB = new Date(startDate);
  let endDateB = new Date(endDate);

  const hoursMap = new Map();
  for (let h = 8; h <= 22; h++) {
    hoursMap.set(h, { revenue: 0, orderIds: new Set() });
  }

  (sales || []).forEach((s) => {
    if (!s.clientdateclienttime) return;

    // Filter by date range if provided
    if (s.clientdateclienttime && (startDateB || endDateB)) {
      const itemDate = new Date(s.clientdateclienttime);
      if (startDateB && itemDate < startDateB) return;
      if (endDateB && itemDate > endDateB) return;
    }

    // Extract hour from ISO string or "18:19:09" format
    let hourNum = -1;
    if (s.clientdateclienttime) {
      hourNum = new Date(s.clientdateclienttime).getHours();
    }

    // Accumulate if hour falls within the 8am - 10pm window
    if (hoursMap.has(hourNum)) {
      const bucket = hoursMap.get(hourNum);
      bucket.revenue += s.total || 0;

      // Use invoice or receipt number to distinguish orders
      const orderId = s.invoiceno || s.receiptno;
      if (orderId) {
        bucket.orderIds.add(orderId.trim());
      }
    }
  });

  return Array.from(hoursMap.entries()).map(([h, data]) => ({
    hour: `${h}:00`,
    revenue: data.revenue,
    orders: data.orderIds.size,
  }));
}

/**
 * @typedef {Object} DayOfWeekEntry
 * @property {string} day
 * @property {number} revenue
 */

/**
 * @param {string} startDate
 * @param {string} endDate
 * @param {import("../models/SalesModel").SalesModel[]} sales
 * @returns {DayOfWeekEntry[]}
 */
export function dayOfWeek(startDate, endDate, sales) {
  const list = lastNMonths(startDate, endDate);

  // let startDateB = list[0].date;
  // let endDateB = list[list.length - 1].date;

  let startDateB = new Date(startDate);
  let endDateB = new Date(endDate);

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const totalsByDayIndex = Array(7).fill(0);

  (sales || []).forEach((s) => {
    if (!s.clientdateclienttime) return;

    const itemDate = new Date(s.clientdateclienttime);

    // Filter by date range if provided
    if (startDateB && itemDate < startDateB) return;
    if (endDateB && itemDate > endDateB) return;

    const dayIdx = itemDate.getDay(); // 0 = Sun, 1 = Mon, ...
    totalsByDayIndex[dayIdx] += s.total || 0;
  });

  // Reorder to return Mon -> Sun sequence
  const displayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon to Sun

  return displayOrder.map((idx) => ({
    day: days[idx],
    revenue: totalsByDayIndex[idx],
  }));
}

// fecthing real data

/**
 * @returns {Promise<import("../models/StoreModel").StoreModel[]>}
 */
export async function getStores() {
  let stores = [];

  try {
    stores = await fetchStoresApi();
  } catch (error) {}

  return stores;
}

/**
 * @returns {Promise<import("../models/SalesAgentsModel").SalesAgentModel[]>}
 */
export async function getSalesAgents() {
  let salesagents = [];

  try {
    salesagents = await fetchSalesAgentsApi();
  } catch (error) {}

  return [
    ...salesagents,
    {
      employeescode: "-1",
      employeesname: "AUTO",
      friendlycode: "-1",
    },
  ];
}

/**
 * @returns {Promise<import("../models/CategoryModel").CategoryModel[]>}
 */
export async function getCategories() {
  let categories = [];

  try {
    categories = await fetchCategoriesApi();
  } catch (error) {}

  return categories;
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise<import("../models/SalesModel").SalesModel[]>}
 */
export async function getSales({ startDate, endDate }) {
  let sales = [];
  try {
    // const tdy = new Date();
    // const tdy2 = new Date();
    // tdy2.setMonth(tdy2.getMonth() - 12);

    // const yearAgo = tdy2.toLocaleDateString("en-CA");
    // let startDate = yearAgo;
    // let endDate = tdy.toLocaleDateString("en-CA");
    const formattedStart = new Date(startDate).toLocaleDateString("en-CA");
    const formattedEnd = new Date(endDate).toLocaleDateString("en-CA");

    sales = await fetchSalesApi(formattedStart, formattedEnd);
    // console.log(sales);
  } catch (error) {}
  return sales;
}

/**
 * @param {Object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise<import("../models/InvoiceModel").InvoiceModel[]>}
 */
export async function getInvoices({ startDate, endDate }) {
  let invoices = [];
  try {
    // const tdy = new Date();
    // const tdy2 = new Date();
    // tdy2.setMonth(tdy2.getMonth() - 12);

    // const yearAgo = tdy2.toLocaleDateString("en-CA");
    // let startDate = yearAgo;
    // let endDate = tdy.toLocaleDateString("en-CA");
    const formattedStart = new Date(startDate).toLocaleDateString("en-CA");
    const formattedEnd = new Date(endDate).toLocaleDateString("en-CA");

    invoices = await fetchInvoiceApi(formattedStart, formattedEnd);
    // console.log(invoices);
  } catch (error) {}
  return invoices;
}
